import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API_URL } from '../types';
import { useTranslation } from '../i18n';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const defaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = defaultIcon;

export interface LatLng {
  lat: number;
  lng: number;
}

export interface LocationMapProps {
  precise?: LatLng | null;
  approximate?: LatLng | null;
  approximateRadiusM?: number;
  height?: number | string;
  interactive?: boolean;
  className?: string;
}

const FitMapView: React.FC<{
  precise?: LatLng | null;
  approximate?: LatLng | null;
  approximateRadiusM: number;
}> = ({ precise, approximate, approximateRadiusM }) => {
  const map = useMap();

  useEffect(() => {
    const points: L.LatLngExpression[] = [];
    if (precise) points.push([precise.lat, precise.lng]);
    if (approximate) points.push([approximate.lat, approximate.lng]);

    if (points.length === 0) return;

    if (points.length === 1 && !precise && approximate) {
      map.setView([approximate.lat, approximate.lng], 15);
      return;
    }

    if (points.length === 1 && precise && !approximate) {
      map.setView([precise.lat, precise.lng], 17);
      return;
    }

    const bounds = L.latLngBounds(points);
    if (approximate && approximateRadiusM > 0) {
      const padLat = approximateRadiusM / 111_320;
      const padLng =
        approximateRadiusM /
        (111_320 * Math.cos((approximate.lat * Math.PI) / 180));
      bounds.extend([approximate.lat + padLat, approximate.lng + padLng]);
      bounds.extend([approximate.lat - padLat, approximate.lng - padLng]);
    }
    map.fitBounds(bounds, { padding: [28, 28], maxZoom: 17 });
  }, [map, precise, approximate, approximateRadiusM]);

  return null;
};

export const LocationMap: React.FC<LocationMapProps> = ({
  precise,
  approximate,
  approximateRadiusM = 200,
  height = 280,
  interactive = true,
  className,
}) => {
  const { t } = useTranslation();

  const center = useMemo((): LatLng => {
    if (precise) return precise;
    if (approximate) return approximate;
    return { lat: 49.8175, lng: 15.473 };
  }, [precise, approximate]);

  const hasMarkers = Boolean(precise || approximate);
  if (!hasMarkers) return null;

  const tileUrl = `${API_URL}/mapy/tiles/basic/{z}/{x}/{y}`;

  return (
    <div className={className ?? 'location-map'}>
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={precise ? 17 : 15}
        style={{ height, width: '100%', borderRadius: 'var(--radius, 8px)' }}
        scrollWheelZoom={interactive}
        dragging={interactive}
        doubleClickZoom={interactive}
        touchZoom={interactive}
      >
        <TileLayer
          url={tileUrl}
          attribution='&copy; <a href="https://mapy.com/">Mapy.com</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        <FitMapView
          precise={precise}
          approximate={approximate}
          approximateRadiusM={approximateRadiusM}
        />
        {approximate && (
          <Circle
            center={[approximate.lat, approximate.lng]}
            radius={approximateRadiusM}
            pathOptions={{ color: '#2563eb', fillColor: '#3b82f6', fillOpacity: 0.2, weight: 2 }}
          />
        )}
        {precise && <Marker position={[precise.lat, precise.lng]} />}
      </MapContainer>
      <p className="location-map-legend text-muted" style={{ fontSize: '0.8rem', marginTop: '0.35rem' }}>
        {precise && approximate && (
          <>
            <span className="location-map-legend__pin">●</span> {t('account.location_precise_pin')}
            {' · '}
            <span className="location-map-legend__area">○</span> {t('account.location_approximate_area')}
          </>
        )}
        {!precise && approximate && (
          <span>
            <span className="location-map-legend__area">○</span> {t('account.location_approximate_area')}
          </span>
        )}
        {precise && !approximate && (
          <span>
            <span className="location-map-legend__pin">●</span> {t('account.location_precise_pin')}
          </span>
        )}
      </p>
    </div>
  );
};
